import { BehaviorSubject, defer, merge, Observable, of, Subject, throwError } from 'rxjs';
import { delay, exhaustMap, filter, first, map, retryWhen, switchMap, tap } from 'rxjs/operators';

import { monitorLogger } from 'src/app/project/monitor/monitor-logger';
import { DateFormatter } from '@core/helpers';
import { ApiService } from '@core/http';
import { environment } from '@env';

import {
  NativeWebsocketChatMessage,
  NativeWebsocketConnectionDetails,
  WebSocketClient,
  WebsocketStatus,
  WebsocketChatMessage,
} from '../../definitions';

const RECONNECT_TIMEOUT_MS = 3000;

export class NativeWebSocketProvider implements WebSocketClient {
  private socket: WebSocket | null;
  private connectionLoopActive = false;

  private _messages$ = new Subject<string>();
  private _status$ = new BehaviorSubject<WebsocketStatus>(null);
  private _reconnected$ = new Subject<void>();

  public readonly messages$ = this._messages$.asObservable();
  public readonly status$ = this._status$.asObservable();
  public readonly reconnected$ = this._reconnected$.asObservable();

  public isDown$: Observable<boolean> = this.status$.pipe(
    map((status) => [WebsocketStatus.Disconnected, WebsocketStatus.Error].includes(status)),
  );

  public isOnline$: Observable<boolean> = this.status$.pipe(map((status) => WebsocketStatus.Connected === status));

  constructor(private apiService: ApiService) {}

  public connect(): Observable<void> {
    if (this.connectionLoopActive) return of(null);
    this.connectionLoopActive = true;

    let connectionsCount = 0;

    const connectionError$ = this.isDown$.pipe(
      filter((isOffline) => isOffline),
      switchMap(() => throwError(new Error('Disconnected'))),
    );
    const onlineSuccess$ = this.isOnline$.pipe(first((isOnline) => isOnline));

    return defer(() => {
      return this.subscribeNotifications().pipe(
        retryWhen((errors) => errors.pipe(delay(3000))),

        switchMap((response) => {
          this.internalConnect(response.socket_url);

          return merge(onlineSuccess$, connectionError$).pipe(
            tap(() => {
              this.send(`0 ${response.session_id}`);
            }),
            map(() => undefined),
          );
        }),

        tap(() => {
          connectionsCount++;
        }),

        exhaustMap(() => connectionError$),

        retryWhen((errors$) =>
          errors$.pipe(
            tap(() => this.disconnect()),
            delay(RECONNECT_TIMEOUT_MS),
          ),
        ),
      );
    });
  }

  public disconnect() {
    if (this.socket) {
      this.resetSocketEventsSubscriptions();
      this.socket.close();
      this.socket = null;
      this._status$.next(WebsocketStatus.Disconnected);
      monitorLogger.socket.trackStatus(WebsocketStatus.Disconnected);
    }
  }

  public destroy() {
    this.disconnect();
    this.connectionLoopActive = false;
    this._status$.next(null);
  }

  public send(message: string | ArrayBufferLike | Blob | ArrayBufferView) {
    if (typeof message === 'string') {
      monitorLogger.socket.trackSend('SEND', {
        data: message,
      });
    } else if (message instanceof Blob) {
      monitorLogger.socket.trackSend('BLOB');
    } else if (message instanceof ArrayBuffer) {
      monitorLogger.socket.trackSend('ARRAY_BUFFER');
    } else if (ArrayBuffer.isView(message)) {
      monitorLogger.socket.trackSend('ARRAY_BUFFER_VIEW');
    }

    this.socket?.send(message);
  }

  public sendChatMessage(chatId: string, message: string): Observable<void> {
    return this.apiService.post(`${environment.environmentVariables.apiChatUrl}/chat/message`, {
      message,
      chat_id: chatId,
    });
  }

  public sendChatAttachment(chatId: string, file: File): Observable<void> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.apiService.postWithForm(
      `${environment.environmentVariables.apiChatUrl}/chat/attach/${chatId}`,
      formData,
    );
  }

  public sendServiceMessage(chatId: string, serviceMessage: string, ...additionalParams: string[]): Observable<void> {
    return this.apiService.post(
      `${environment.environmentVariables.apiChatUrl}/chat/service`,
      JSON.stringify({
        type: serviceMessage,
        chat_id: chatId,
        ...additionalParams,
      }),
    );
  }

  public getChatMessagesById(
    chatId: string,
    count: number,
    from: Date = new Date(),
  ): Observable<WebsocketChatMessage[]> {
    return this.apiService.get<NativeWebsocketChatMessage[]>(
      `${environment.environmentVariables.apiChatUrl}/chat/messages/${chatId}/${count}/${DateFormatter.dateIncludeHours(
        from,
        4,
      )}`,
    );
  }

  private internalConnect(url: string): void {
    this.disconnect();
    this.socket = new WebSocket(url);
    this._status$.next(WebsocketStatus.Connecting);
    this.subscribeSocketEvents();
    monitorLogger.socket.trackStatus(WebsocketStatus.Connecting, { url });
  }

  private subscribeNotifications(): Observable<NativeWebsocketConnectionDetails> {
    return this.apiService.post(`${environment.environmentVariables.apiChatUrl}/nc/subscribe`);
  }

  private subscribeSocketEvents() {
    this.socket.onopen = () => this.handleOpenConnection();
    this.socket.onerror = (err: Event) => this.handleSocketError(err);
    this.socket.onmessage = (messageEvent: MessageEvent) => this.handleReceiveMessage(messageEvent);
    this.socket.onclose = () => this.handleCloseConnection();
  }

  private resetSocketEventsSubscriptions() {
    this.socket.onopen = null;
    this.socket.onerror = null;
    this.socket.onmessage = null;
    this.socket.onclose = null;
  }

  private handleSocketError(err: Event) {
    monitorLogger.socket.trackError(err);
    console.error('Socket ERROR', err); // todo: debug only
    this._status$.next(WebsocketStatus.Error);
  }

  private handleOpenConnection() {
    this._status$.next(WebsocketStatus.Connected);
    monitorLogger.socket.trackStatus(WebsocketStatus.Connected);
  }

  private handleReceiveMessage(message: MessageEvent) {
    monitorLogger.socket.trackMessage(message.data);
    this._messages$.next(message.data);
  }

  private handleCloseConnection() {
    this._status$.next(WebsocketStatus.Disconnected);
  }
}
